// Pure, geometry-based hit-testing for the canvas.
//
// The canvas does NOT use Konva's internal hit graph (its hit canvas drifted
// out of sync with the scene in production builds — pan/zoom moved shapes
// visually while the clickable regions stayed put, so clicks landed on the
// wrong object or nothing at all). Instead the shape layers render with
// `listening={false}` and every pointer interaction is resolved here, in
// world coordinates, against the live shape list. Deterministic, always in
// sync with what's on screen, and unit-testable.

import type { Shape } from '@frame/shared';
import {
  RESIZE_HANDLES,
  handleCenter,
  isCornerHandle,
  type ResizeHandle,
} from './resize';

export interface WorldPt {
  x: number;
  y: number;
}
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Screen-pixel tolerances, divided by scale at call time so they stay
// constant on screen at any zoom.
const THIN_HIT_PX = 8; // arrows / freehand strokes
const HANDLE_HIT_PX = 12; // matches SelectionHandles' HIT_SIZE
// Text shapes carry no width/height; approximate a clickable box from the
// font size (mirrors the legacy shapeBBox fallback).
const TEXT_HIT_W = 200;

function pointInRect(p: WorldPt, b: Box): boolean {
  return p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h;
}

// Shortest distance from point p to segment ab.
function distToSegment(p: WorldPt, a: WorldPt, b: WorldPt): number {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const wx = p.x - a.x;
  const wy = p.y - a.y;
  const len2 = vx * vx + vy * vy;
  let t = len2 === 0 ? 0 : (wx * vx + wy * vy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + t * vx;
  const cy = a.y + t * vy;
  return Math.hypot(p.x - cx, p.y - cy);
}

// World-space hit-test of a single shape. `scale` is the viewport scale so
// thin-shape tolerances stay a constant screen size.
export function pointInShape(shape: Shape, p: WorldPt, scale: number): boolean {
  switch (shape.type) {
    case 'sticky':
    case 'rect':
    case 'image':
    case 'frame':
      return pointInRect(p, { x: shape.x, y: shape.y, w: shape.w, h: shape.h });
    case 'ellipse': {
      const rx = shape.w / 2;
      const ry = shape.h / 2;
      if (rx <= 0 || ry <= 0) return false;
      const nx = (p.x - (shape.x + rx)) / rx;
      const ny = (p.y - (shape.y + ry)) / ry;
      return nx * nx + ny * ny <= 1;
    }
    case 'text': {
      const h = (shape.fontSize ?? 16) + 8;
      return pointInRect(p, { x: shape.x, y: shape.y, w: TEXT_HIT_W, h });
    }
    case 'arrow': {
      if (shape.from.kind !== 'point' || shape.to.kind !== 'point') return false;
      const tol = THIN_HIT_PX / scale;
      return distToSegment(p, shape.from, shape.to) <= tol;
    }
    case 'freehand': {
      const pts = shape.points;
      const tol = THIN_HIT_PX / scale + shape.strokeWidth / 2;
      for (let i = 0; i + 3 < pts.length; i += 2) {
        const a = { x: pts[i]!, y: pts[i + 1]! };
        const b = { x: pts[i + 2]!, y: pts[i + 3]! };
        if (distToSegment(p, a, b) <= tol) return true;
      }
      return false;
    }
    default:
      return false;
  }
}

// Topmost shape under the point, or null. `shapes` is expected in ascending
// z / render order (what useShapes returns), so we scan back-to-front.
export function pickTopShape(
  shapes: readonly Shape[],
  p: WorldPt,
  scale: number,
): string | null {
  for (let i = shapes.length - 1; i >= 0; i--) {
    const s = shapes[i]!;
    if (pointInShape(s, p, scale)) return s.id;
  }
  return null;
}

// Which resize handle (if any) is under the point, for a shape whose bbox is
// `bbox` (world coords). `layout` mirrors SelectionHandles: stickies expose
// corners + E/W only.
export function pickHandle(
  bbox: Box,
  p: WorldPt,
  scale: number,
  layout: 'all' | 'sticky',
): ResizeHandle | null {
  const half = HANDLE_HIT_PX / scale / 2;
  const handles =
    layout === 'sticky'
      ? RESIZE_HANDLES.filter((h) => isCornerHandle(h) || h === 'e' || h === 'w')
      : RESIZE_HANDLES;
  for (const h of handles) {
    const c = handleCenter(h, bbox.w, bbox.h);
    const hx = bbox.x + c.x;
    const hy = bbox.y + c.y;
    if (Math.abs(p.x - hx) <= half && Math.abs(p.y - hy) <= half) return h;
  }
  return null;
}
